
import React from 'react';
import {
  X, RotateCcw, User, Search, Package, CheckCircle
} from 'lucide-react';
import { Modal, FormInput, FormSelect, Button } from '../../../components/ui';
import { fmt } from '../../../lib/formatters';
import { useSaleReturn } from '../hooks/useSaleReturn';

export const SaleReturnModal = ({ isOpen, onClose, onSuccess, dynamicSettings, initialInvoiceNo, serviceType }) => {
  const {
    form, setForm,
    rows, updateRow,
    loading, fetching,
    searchText, setSearchText,
    showDropdown, setShowDropdown,
    filteredInvoices,
    handleFetchInvoice,
    handleLoadSale,
    totalRefund,
    handleSubmit,
    toggleRowSelection
  } = useSaleReturn({ isOpen, initialInvoiceNo, serviceType, dynamicSettings, onSuccess, onClose });

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex flex-col gap-0.5 z-10 relative">
          <span className="text-[14px] font-bold text-white font-serif tracking-widest uppercase">
            VERVE KITCHENS
          </span>
          <span className="text-[9px] text-slate-300 font-semibold tracking-widest uppercase">
            Record Sale Return
          </span>
        </div>
      }
      size="max-w-2xl"
    >
      <div className="max-h-[75vh] overflow-y-auto pr-2 no-scrollbar pb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">

          {/* Section 1: Return Information */}
          <div className="col-span-2 text-[11px] font-black text-slate-400 uppercase tracking-widest mt-2 mb-2 border-b border-slate-50 pb-1 flex items-center gap-2">
            <span>01. Return Information</span>
          </div>

          <div className="relative flex flex-col gap-1.5">
            <label className="label text-brand-blue text-[11px] font-bold uppercase tracking-widest">Invoice Number * (Search & Select)</label>
            <div className="flex gap-2">
              <div className="relative flex-1 ">
                <FormInput
                  leftIcon={<Search size={14} />}
                  inputClassName="w-full text-left bg-blue-50/20 border-blue-100 focus:border-brand-blue font-medium text-brand-blue placeholder:text-slate-300"
                  placeholder="Search invoice no..."
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value.toUpperCase());
                    setShowDropdown(true);
                  }}
                  onFocus={() => setShowDropdown(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && searchText) {
                      handleFetchInvoice(searchText);
                    }
                  }}
                  hideError={true}
                />

                {showDropdown && (
                  <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-2xl max-h-[250px] overflow-y-auto no-scrollbar animate-in fade-in slide-in-from-top-2">
                    <div className="p-2 border-b border-slate-50 flex justify-between items-center sticky top-0 bg-white/90 backdrop-blur-sm">
                      <span className="text-[10px] font-semibold text-slate-400">Recent Invoices</span>
                      <button onClick={() => setShowDropdown(false)} className="text-slate-300 hover:text-slate-600 transition-colors">
                        <X size={12} />
                      </button>
                    </div>
                    {filteredInvoices.length > 0 ? (
                      filteredInvoices.map(p => {
                        const invNo = p.invoiceNo || p.invoiceNumber || p.billNo || "N/A";
                        return (
                          <button
                            key={p._id}
                            className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-0 group"
                            onClick={() => handleFetchInvoice(invNo)}
                          >
                            <div className="flex justify-between items-center mb-0.5">
                              <span className="text-brand-blue text-sm tracking-tight">
                                {invNo}
                              </span>
                              <span className="text-[9px] font-medium text-slate-400 tracking-tight">
                                {p.date ? new Date(p.date).toLocaleDateString() : ""}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <User size={10} className="text-slate-400" />
                              <span className="text-[10px] font-medium text-slate-600 truncate">
                                {invNo}
                              </span>
                            </div>
                          </button>
                        );
                      })
                    ) : (
                      <div className="p-8 text-center">
                        <Package className="mx-auto text-slate-200 mb-2" size={24} />
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">No results</p>
                      </div>
                    )}
                  </div>
                )}

                {showDropdown && (
                  <div
                    className="fixed inset-0 z-40 bg-transparent"
                    onClick={() => setShowDropdown(false)}
                  />
                )}
              </div>

              <button
                type="button"
                className="min-w-[120px] cursor-pointer py-2.5 min-h-[38px] bg-slate-900 hover:bg-black text-white rounded-md text-[12px] font-bold transition-all duration-300 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed border border-slate-900 shadow-sm flex items-center justify-center"
                onClick={() => handleFetchInvoice(searchText)}
                disabled={fetching || !searchText}
              >
                {fetching ? 'Fetching...' : 'Fetch'}
              </button>
            </div>
          </div>

          <FormInput
            label="Customer (Autofill)"
            value={form.customer || 'Select Customer'}
            readOnly
            inputClassName="bg-slate-50 text-slate-500 font-bold"
          />

          <div className="col-span-2">
            <FormInput
              label="Return Reason *"
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="e.g. Damaged goods, quality issue..."
              required
              inputClassName="border-brand-blue"
              labelClassName="text-brand-blue font-black"
            />
          </div>

          <FormInput
            type="date"
            label="Invoice Date"
            value={form.invoiceDate}
            readOnly
            inputClassName="bg-slate-50 text-slate-400"
          />

          <FormInput
            label="Return Bill # / DR Note #"
            value={form.billNo}
            onChange={(e) => setForm({ ...form, billNo: e.target.value })}
            placeholder="PR-912357"
            inputClassName="font-bold"
          />

          {/* Section 2: Line Items Configuration */}
          <div className="col-span-2 text-[11px] font-black text-slate-400 uppercase tracking-widest mt-6 mb-2 border-b border-slate-50 pb-1 flex items-center gap-2">
            <span>02. <span className="px-1">LINE ITEMS CONFIGURATION</span></span>
          </div>

          <div className="col-span-2 space-y-6">
            {rows.map((it, idx) => (
              <div key={it._id} className={`p-6 rounded-2xl border-2 transition-all duration-300 relative overflow-hidden ${it.selected ? 'bg-slate-50/50 border-slate-300 shadow-xl shadow-slate-200/50' : 'bg-white border-slate-100 opacity-80'}`}>

                {/* Selection Indicator */}
                <div
                  onClick={() => toggleRowSelection(idx)}
                  className={`absolute top-0 left-0 w-1.5 h-full transition-all duration-300 cursor-pointer ${it.selected ? 'bg-slate-900' : 'bg-slate-100 hover:bg-slate-200'}`}
                />

                <div className="flex items-center justify-between gap-4 mb-6">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-md flex items-center justify-center text-xs font-black transition-all ${it.selected ? 'bg-slate-900 text-white rotate-12 shadow-lg shadow-slate-300' : 'bg-slate-100 text-slate-400'}`}>
                      {idx + 1}
                    </div>
                    <div>
                      <h4 className={`text-base font-black tracking-tight mb-1 transition-colors ${it.selected ? 'text-slate-800' : 'text-slate-400'}`}>{it.name}</h4>
                      <div className="flex flex-wrap gap-2">
                        <span className={`text-[9px] font-black px-2 py-0.5 rounded uppercase tracking-wider transition-colors ${it.selected ? 'text-brand-blue bg-blue-50' : 'text-slate-300 bg-slate-50'}`}>SKU: {it.itemCode}</span>
                        <span className={`text-[9px] font-black px-2 py-0.5 rounded uppercase tracking-wider transition-colors ${it.selected ? 'text-slate-400 bg-slate-50' : 'text-slate-200 bg-slate-50'}`}>{it.brand || 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => toggleRowSelection(idx)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-[10px] font-black uppercase tracking-widest transition-all ${it.selected ? 'bg-slate-900 text-white shadow-sm' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                  >
                    {it.selected ? <CheckCircle size={14} /> : null}
                    {it.selected ? 'Selected' : 'Select to Return'}
                  </button>
                </div>

                <div className={`grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5 transition-all duration-300 ${it.selected ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
                  <FormInput
                    label="HSN Code"
                    value={it.hsnCode || 'N/A'}
                    readOnly
                    inputClassName="bg-white/50 text-slate-400 font-bold border-slate-100"
                  />
                  <FormInput
                    label="Sales Unit"
                    value={it.unit || 'Units'}
                    readOnly
                    inputClassName="bg-white/50 text-slate-400 font-bold border-slate-100"
                  />
                  <FormInput
                    label="Purchase Rate (₹)"
                    value={`${fmt(it.rate)}`}
                    readOnly
                    inputClassName="bg-white/50 text-slate-400 font-bold border-slate-100"
                  />
                  <div className="relative">
                    <FormInput
                      type="number"
                      label="Return Quantity *"
                      labelClassName="text-brand-blue font-bold"
                      inputClassName={`font-black h-[42px] ${it.selected ? 'border-brand-blue text-brand-blue bg-white ring-2 ring-brand-blue/5' : 'bg-slate-50'}`}
                      value={it.returnedQty}
                      onChange={(e) => updateRow(idx, Number(e.target.value))}
                      min={1}
                      max={it.maxReturn}
                    />
                    <div className="absolute right-3 top-[36px] text-[8px] font-black text-slate-300 uppercase">
                      AVAILABLE: {it.maxReturn}
                    </div>
                  </div>
                  <FormInput
                    label="Tax (%)"
                    value={`${it.taxPercent || 0}%`}
                    readOnly
                    inputClassName="bg-white/50 text-slate-400 font-bold border-slate-100"
                  />
                  <div>
                    <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Return Value (₹)</label>
                    <div className="input bg-slate-900 text-white font-black flex items-center h-[42px] px-4 rounded-md border border-slate-800 shadow-inner">
                      {fmt(it.returnedQty * it.rate)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Section 3: Shipping & Logistics */}
          <div className="col-span-2 text-[11px] font-black text-slate-400 uppercase tracking-widest mt-8 mb-2 border-b border-slate-50 pb-1 flex items-center gap-2">
            <span>04. SHIPPING & LOGISTICS</span>
          </div>

          {/* <FormInput
            label="STORE LOCATION (SOURCE)"
            value={form.store}
            readOnly
            inputClassName="bg-slate-50 text-slate-400 font-bold"
            labelClassName="text-brand-blue font-bold"
          /> */}

          <FormInput
            label="CUSTOMER LOCATION (DESTINATION)"
            value={form.customer}
            readOnly
            inputClassName="bg-slate-50 text-slate-400 font-bold"
            labelClassName="text-brand-orange font-bold"
          />

          <FormSelect
            label="Mode of Transport"
            value={form.transportMode}
            onChange={(e) => setForm({ ...form, transportMode: e.target.value })}
            options={[
              { value: 'Road', label: 'Road / Truck' },
              { value: 'Bike', label: 'Bike / Scooter' },
              { value: 'Courier', label: 'Courier / Cargo' },
              { value: 'Self', label: 'Self Pick-up' }
            ]}
          />

          <FormInput
            label="Vehicle Number"
            value={form.vehicleNo}
            onChange={(e) => setForm({ ...form, vehicleNo: e.target.value })}
            placeholder="MH 12 AB 1234"
          />

          <div className="col-span-2">
            <FormInput
              label="Tracking / LR Number"
              value={form.trackingNo}
              onChange={(e) => setForm({ ...form, trackingNo: e.target.value })}
              placeholder="TRK12345678"
            />
          </div>
        </div>

        {/* Final Summary Highlight (Dark Payout Box) */}
        <div className="mt-10">
          <div className="p-8 rounded-2xl bg-slate-900 flex flex-col md:flex-row md:items-center justify-between gap-10 relative overflow-hidden group">
            <div className="absolute right-0 top-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16 blur-3xl group-hover:bg-white/10 transition-all duration-700" />
            <div className="space-y-2 relative z-10">
              <div className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-brand-blue animate-pulse" />
                <div className="text-[10px] font-black text-brand-blue uppercase tracking-[0.4em]">Final Return Payout</div>
              </div>
              <div className="flex gap-4">
                <span className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">TAXABLE: {fmt(totalRefund)}</span>
                <span className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">TAX: ₹0</span>
              </div>
            </div>
            <div className="text-4xl font-black text-white tracking-tighter relative z-10">
              {fmt(totalRefund)}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-4 border-t border-slate-100 mt-2 pb-6 px-2">
          <button
            type="button"
            onClick={onClose}
            className="w-full sm:flex-1 cursor-pointer py-2.5 border border-slate-200 hover:bg-slate-50 text-slate-800 rounded-md text-[12px] font-semibold transition-all duration-300 ease-in-out shadow-sm"
          >
            Cancel
          </button>
          <button 
            type="button" 
            onClick={handleSubmit} 
            disabled={loading}
            className="w-full sm:flex-1 cursor-pointer py-2.5 min-h-[38px] bg-slate-900 hover:bg-black text-white rounded-md text-[12px] font-bold transition-all duration-300 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed border border-slate-900 shadow-sm flex items-center justify-center gap-2"
          >
            <RotateCcw size={16} />
            Complete Return Entry
          </button>
        </div>
      </div>
    </Modal>
  );
};

